import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { Broadcast, ArrowClockwise } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sep 22 2026, user's explicit ask ("Webhook Health Dashboard") - an
// at-a-glance count per SAP Business Object + when it last fired, so
// staff can tell a subscriber is actually live without reading raw
// webhook payloads one by one (see /admin/webhooks/sap-events/summary).
const LIVE_THRESHOLD_MINUTES = 15;

function minutesAgo(isoString) {
  if (!isoString) return null;
  return (Date.now() - new Date(isoString).getTime()) / 60000;
}

function formatAgo(isoString) {
  const mins = minutesAgo(isoString);
  if (mins === null) return "Never";
  if (mins < 1) return "Just now";
  if (mins < 60) return `${Math.round(mins)} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} hr ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

export default function WebhookHealthPage() {
  const [summary, setSummary] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: summaryData }, { data: eventsData }] = await Promise.all([
        axios.get(`${API}/admin/webhooks/sap-events/summary`),
        axios.get(`${API}/admin/webhooks/sap-events`, { params: { limit: 15 } }),
      ]);
      setSummary(summaryData.summary || []);
      setRecentEvents(eventsData.events || []);
    } catch (err) {
      toast.error("Could not load webhook health", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <Broadcast size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="webhook-health-title">
              SAP Webhook Health
            </h1>
            <p className="text-sm text-[#667085] font-sans mt-0.5">
              Live event counts per Business Object subscriber - "Live" means an event landed in the last {LIVE_THRESHOLD_MINUTES} minutes.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="webhook-health-refresh-button" className="gap-1.5 shrink-0">
            <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-md overflow-x-auto mb-6">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085] font-sans">
                <th className="px-3 py-2 font-medium">Business Object / Event Type</th>
                <th className="px-3 py-2 font-medium text-right">Total Events</th>
                <th className="px-3 py-2 font-medium">Last Received</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="webhook-health-loading">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && summary.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="webhook-health-empty">
                    No webhook events received yet - activate a SAP Event Notification subscriber to see it here.
                  </td>
                </tr>
              )}
              {summary.map((row) => {
                const isLive = (minutesAgo(row.last_received_at) ?? Infinity) <= LIVE_THRESHOLD_MINUTES;
                return (
                  <tr key={row.event_type} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`webhook-health-row-${row.event_type}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-[#1D2939]">{row.event_type}</td>
                    <td className="px-3 py-2 text-right text-[#475467]" data-testid={`webhook-health-count-${row.event_type}`}>{row.count}</td>
                    <td className="px-3 py-2 text-[#475467]">{formatAgo(row.last_received_at)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isLive ? "bg-[#ECFDF3] text-[#027A48]" : "bg-[#FEF3F2] text-[#B42318]"
                        }`}
                        data-testid={`webhook-health-status-${row.event_type}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-[#12B76A]" : "bg-[#F04438]"}`} />
                        {isLive ? "Live" : "Quiet"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h2 className="text-sm font-semibold text-[#1D2939] font-heading mb-2">Most Recent Events</h2>
        <div className="bg-white border border-[#D0D5DD] rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085] font-sans">
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Received At</th>
                <th className="px-3 py-2 font-medium">Payload</th>
              </tr>
            </thead>
            <tbody>
              {!loading && recentEvents.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-[#667085] text-sm" data-testid="webhook-health-recent-empty">
                    Nothing yet.
                  </td>
                </tr>
              )}
              {recentEvents.map((e) => (
                <tr key={e._id} className="border-b border-[#F2F4F7] hover:bg-slate-50" data-testid={`webhook-health-recent-${e._id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#1D2939]">{e.event_type}</td>
                  <td className="px-3 py-2 text-[#475467] whitespace-nowrap">{formatAgo(e.received_at)}</td>
                  <td className="px-3 py-2 text-[#475467] font-mono text-xs truncate max-w-[400px]">
                    {JSON.stringify(e.parsed_body || {})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
